import React from "react";
import { Lock, Sparkles, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";
import PortfolioDashboardView from "@/components/portfolio/PortfolioDashboardView";

/**
 * Phase 8.WLNAV.v1.5 — Locked Portfolio Preview (thin wrapper).
 *
 * The preview is now a **thin wrapper** around the shared
 * `PortfolioDashboardView`. It renders the exact same presentational
 * component tree as the real Premium Portfolio, using purely static
 * demo data, with `previewMode=true` so:
 *
 *   • Refresh meta strip, completeness warning, AI-generation banner
 *     are hidden.
 *   • KPI cards 3–5 (Upside, Unrealized P/L, Weighted Score) blur
 *     their values in place.
 *   • Holdings rows 3+ (from META onward) blur cell contents in place
 *     while preserving row borders and structure.
 *   • P/L Breakdown blurs all 3 values in place.
 *   • Closed Positions renders as a static, non-interactive collapsed
 *     header (with count badge).
 *   • AI Portfolio Review renders with title + section headings sharp,
 *     but body/bullets/confidence blurred in place.
 *   • A CTA overlay is rendered on top of the chart (desktop) and in
 *     document flow after the chart (mobile).
 *
 * Security contract:
 *   • NEVER fetches real Premium payloads.
 *   • NEVER receives protected values via props from the API.
 *   • NEVER calls FMP, LLM or portfolio-refresh endpoints.
 *   • NEVER calls `/api/portfolio/performance` — the growth chart is
 *     force-fed a static demo payload via `chartFetchOverride`.
 *   • All values below are hard-coded.
 */

// ────────────────────────────────────────────────────────────────
// DEMO DATA — shaped like `GET /api/portfolio`
//
// The demo holdings order is per Message 362: rows 1 (MSFT), 2 (GOOGL)
// remain readable; rows 3+ (META, JPM, JNJ, V, MU) are blurred by the
// HoldingsTable in previewMode using `revealedRowCount=2`.
// ────────────────────────────────────────────────────────────────

const DEMO_HOLDINGS = [
  { holding_id: "demo-msft",  ticker: "MSFT",  company_name: "Microsoft Corp.",   shares: 12,  buy_price: 320.10, current_price: 432.00, market_value: 5184.00, unrealized_pnl: 1342.80, unrealized_pnl_pct: 0.349,  portfolio_weight: 0.271, fair_value: 465.00, upside_downside_pct: 0.076,  ai_score: 74, risk_level: "low",    currency: "USD" },
  { holding_id: "demo-googl", ticker: "GOOGL", company_name: "Alphabet Inc.",     shares: 15,  buy_price: 148.30, current_price: 176.80, market_value: 2652.00, unrealized_pnl: 427.50,  unrealized_pnl_pct: 0.192,  portfolio_weight: 0.139, fair_value: 190.00, upside_downside_pct: 0.075,  ai_score: 71, risk_level: "low",    currency: "USD" },
  { holding_id: "demo-meta",  ticker: "META",  company_name: "Meta Platforms",    shares: 6,   buy_price: 412.10, current_price: 522.60, market_value: 3135.60, unrealized_pnl: 663.00,  unrealized_pnl_pct: 0.268,  portfolio_weight: 0.164, fair_value: 540.00, upside_downside_pct: 0.033,  ai_score: 70, risk_level: "low",    currency: "USD" },
  { holding_id: "demo-jpm",   ticker: "JPM",   company_name: "JPMorgan Chase",    shares: 20,  buy_price: 155.30, current_price: 197.10, market_value: 3942.00, unrealized_pnl: 836.00,  unrealized_pnl_pct: 0.269,  portfolio_weight: 0.206, fair_value: 205.00, upside_downside_pct: 0.040,  ai_score: 68, risk_level: "medium", currency: "USD" },
  { holding_id: "demo-jnj",   ticker: "JNJ",   company_name: "Johnson & Johnson", shares: 14,  buy_price: 162.40, current_price: 156.10, market_value: 2185.40, unrealized_pnl: -88.20,  unrealized_pnl_pct: -0.039, portfolio_weight: 0.114, fair_value: 172.00, upside_downside_pct: 0.102,  ai_score: 63, risk_level: "low",    currency: "USD" },
  { holding_id: "demo-v",     ticker: "V",     company_name: "Visa Inc.",         shares: 8,   buy_price: 232.40, current_price: 268.10, market_value: 2144.80, unrealized_pnl: 285.60,  unrealized_pnl_pct: 0.154,  portfolio_weight: 0.063, fair_value: 275.00, upside_downside_pct: 0.026,  ai_score: 76, risk_level: "low",    currency: "USD" },
  { holding_id: "demo-mu",    ticker: "MU",    company_name: "Micron Technology", shares: 10,  buy_price:  82.10, current_price:  74.30, market_value:  743.00, unrealized_pnl: -78.00,  unrealized_pnl_pct: -0.095, portfolio_weight: 0.043, fair_value:  88.00, upside_downside_pct: 0.184,  ai_score: 58, risk_level: "high",   currency: "USD" },
];

const DEMO_TOTAL_MV      = DEMO_HOLDINGS.reduce((sum, h) => sum + h.market_value, 0);
const DEMO_TOTAL_COST    = DEMO_HOLDINGS.reduce((sum, h) => sum + h.buy_price * h.shares, 0);
const DEMO_TOTAL_FV      = DEMO_HOLDINGS.reduce((sum, h) => sum + h.fair_value * h.shares, 0);
const DEMO_UNREAL_PNL    = DEMO_TOTAL_MV - DEMO_TOTAL_COST;
const DEMO_UNREAL_PCT    = DEMO_UNREAL_PNL / DEMO_TOTAL_COST;                       // decimal fraction
const DEMO_UPSIDE_PCT    = (DEMO_TOTAL_FV - DEMO_TOTAL_MV) / DEMO_TOTAL_MV;         // decimal fraction

const DEMO_SUMMARY = {
  total_market_value: DEMO_TOTAL_MV,
  total_cost_basis: DEMO_TOTAL_COST,
  total_unrealized_pnl: DEMO_UNREAL_PNL,
  total_unrealized_pnl_pct: DEMO_UNREAL_PCT,
  estimated_portfolio_fair_value: DEMO_TOTAL_FV,
  estimated_portfolio_upside_downside_pct: DEMO_UPSIDE_PCT,
  weighted_ai_opportunity_score: 68,
  weighted_risk_level: "medium",
  data_completeness: 1.0,
  excluded_holdings_count: 0,
  insights: {
    top_opportunity:       { ticker: "MU",   value: 0.184 },
    largest_valuation_risk:{ ticker: "META", value: 0.033 },
    highest_weight:        { ticker: "MSFT", value: 0.271 },
    biggest_winner:        { ticker: "MSFT", value: 0.349 },
    biggest_loser:         { ticker: "MU",   value: -0.095 },
    concentration_pct:     0.271,
  },
};

const DEMO_PL_BREAKDOWN = {
  unrealized_pl_total: DEMO_UNREAL_PNL,
  realized_pl_total: 1289.00,
  total_pl: DEMO_UNREAL_PNL + 1289.00,
  closed_positions_count: 2,
  sell_count: 2,
  total_sell_proceeds: 4820.00,
  total_sell_fees: 12.00,
};

const DEMO_REVIEW = {
  generated_at: "2026-01-15T09:00:00.000Z",
  _selected_id: "demo",
  review_locale: "en",
  review: {
    source: "llm",
    review_locale: "en",
    summary:
      "This example portfolio blends high-quality large-cap technology with defensive names in healthcare and payments. Growth exposure is strong, though concentration risk in the top position and elevated valuations in a few names warrant ongoing review.",
    strengths: [
      "Balanced exposure between growth technology and defensive dividend payers.",
      "Two positions trade below Fair Value with double-digit upside potential.",
      "Overall business quality is high; interest-coverage and free-cash-flow margins are strong.",
    ],
    weaknesses: [
      "Top holding accounts for over 27% of portfolio value — concentration risk is meaningful.",
      "One position trades close to Fair Value with limited margin of safety.",
      "Technology weighting is elevated relative to a diversified benchmark.",
    ],
    concentration_risks: [
      { name: "Technology sector", severity: "Medium", explanation: "≈57% weight across MSFT, GOOGL and META." },
      { name: "Top position", severity: "Medium", explanation: "MSFT alone is 27% of the portfolio." },
    ],
    research_priorities: [
      "Re-underwrite MSFT thesis given elevated multiple and enterprise AI capex trajectory.",
      "Reassess MU cycle exposure and inventory correction risk.",
      "Confirm JNJ litigation reserves before adding.",
    ],
    top_opportunity:       { ticker: "MU",   explanation: "Trades below estimated Fair Value with cyclical recovery optionality." },
    largest_valuation_risk:{ ticker: "META", explanation: "Rich multiple after 12M rerating; earnings sensitivity is elevated." },
    confidence:
      "Moderate — assumptions are based on trailing fundamentals and consensus estimates; results are illustrative and not personal advice.",
  },
};

const DEMO_DATA = {
  locked: false,
  summary: DEMO_SUMMARY,
  positions: DEMO_HOLDINGS,
  snapshots: [],
  review: DEMO_REVIEW,
  review_meta: { cached: true, is_stale: false, review_locale: "en" },
  refresh: {
    prices:  { next_available_at: null },
    analysis: { next_available_at: null, refreshes_left_today: 3, daily_limit: 3, last_refresh_at: null, daily_reset_at: null },
  },
  pl_breakdown: DEMO_PL_BREAKDOWN,
  admin_qa_bypass: false,
};

// Closed-positions section header shows a "2" badge per Message 362.
// The header is static in preview mode — no rows are ever rendered
// because ClosedPositionsTable renders a static header only when
// `previewMode=true`. The array length drives the count badge.
const DEMO_CLOSED_POSITIONS = [
  { closed_position_id: "demo-1" },
  { closed_position_id: "demo-2" },
];

// ────────────────────────────────────────────────────────────────
// Demo `performance` payload consumed by PortfolioGrowthChart
// via `fetchOverride`. Same shape as `GET /api/portfolio/performance`.
// Portfolio +18.7%, S&P 500 +12.1%, Fair Value moderately above.
// ────────────────────────────────────────────────────────────────
function buildDemoPerformance(_range) {
  // 13 monthly points across the last year, base index = 100.
  const monthlyPortfolio = [100, 101.5, 103.2, 105.4, 107.0, 108.9, 110.4, 112.2, 113.9, 115.6, 116.7, 117.9, 118.7];
  const monthlyBenchmark = [100, 100.8, 101.9, 103.1, 104.2, 105.5, 106.8, 107.9, 109.0, 110.1, 110.9, 111.5, 112.1];

  const today = new Date();
  const dates = [];
  for (let i = 12; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    dates.push(d.toISOString().slice(0, 10));
  }

  // Base portfolio value that gently grows in line with the index.
  const baseValue = DEMO_TOTAL_COST; // ~ $17.5k
  const baseFairValue = DEMO_TOTAL_FV / DEMO_TOTAL_MV; // ratio ~1.05

  const portfolio_series = dates.map((date, i) => ({
    date,
    index: monthlyPortfolio[i],
    value: baseValue * (monthlyPortfolio[i] / 100),
  }));
  const benchmark_series = dates.map((date, i) => ({
    date,
    index: monthlyBenchmark[i],
    value: monthlyBenchmark[i],
  }));
  const fair_value_series = dates.map((date, i) => ({
    date,
    value: baseValue * (monthlyPortfolio[i] / 100) * baseFairValue,
  }));

  return {
    performance_method: "TWR_DAILY",
    fallback_used: false,
    chart_display_mode: "index",
    data_quality: "good",
    portfolio_return_pct: 18.7,
    benchmark_return_pct: 12.1,
    relative_performance_pct_points: 6.6,
    fair_value_coverage_pct: 100,
    benchmark: { status: "present" },
    performance_history_start_date: dates[0],
    excluded_pre_baseline_snapshot_dates: [],
    portfolio_series,
    benchmark_series,
    fair_value_series,
    cash_flow_summary: null,
  };
}

async function demoPerformanceFetchOverride(range) {
  // Kept async to match the chart's expected signature, but resolves
  // synchronously with a static payload — no network I/O.
  return buildDemoPerformance(range);
}

// ────────────────────────────────────────────────────────────────
// CTA card — copy PRESERVED VERBATIM from the approved v1.4 spec.
// Do NOT redesign or alter this component without explicit approval.
// ────────────────────────────────────────────────────────────────
function CtaCard({ isLoggedOut, onSignup, onLogin, onUpgrade, onViewPricing, t }) {
  const tt = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };
  if (isLoggedOut) {
    return (
      <div
        className="w-full max-w-sm rounded-2xl bg-[rgba(18,18,23,0.9)] backdrop-blur-xl border border-white/15 p-5 shadow-2xl text-center"
        data-testid="portfolio-preview-cta-loggedout"
      >
        <div className="mx-auto inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#3DEBFF]/15 border border-[#3DEBFF]/40 mb-2.5">
          <Lock className="w-4 h-4 text-[#3DEBFF]" />
        </div>
        <div className="font-display text-lg mb-1">
          {tt("portfolio.loggedout.title", "Understand your entire portfolio with Stockaily.")}
        </div>
        <p className="text-xs text-muted-foreground mb-3.5 leading-relaxed">
          {tt("portfolio.loggedout.body", "Track performance, Fair Value, opportunity and risk across every holding.")}
        </p>
        <div className="flex flex-col sm:flex-row items-stretch justify-center gap-2">
          <Button
            size="sm"
            onClick={onSignup}
            data-testid="portfolio-loggedout-signup-button"
            className="shadow-[0_0_0_1.5px_rgba(61,235,255,0.5),0_0_16px_rgba(61,235,255,0.22)] w-full sm:w-auto"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {tt("portfolio.loggedout.signup_cta", "Create free account")}
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onLogin}
            data-testid="portfolio-loggedout-login-button"
            className="w-full sm:w-auto"
          >
            {tt("portfolio.loggedout.login_cta", "Log in")}
          </Button>
        </div>
        <div className="mt-2.5 text-[11px] text-muted-foreground">
          {tt("portfolio.loggedout.footnote", "Free account. No card required.")}
        </div>
        <button
          type="button"
          onClick={onViewPricing}
          data-testid="portfolio-loggedout-view-premium-link"
          className="mt-1.5 text-[11px] font-medium text-[#3DEBFF] hover:underline"
        >
          {tt("portfolio.loggedout.view_premium_link", "View Premium features")}
        </button>
      </div>
    );
  }
  return (
    <div
      className="w-full max-w-sm rounded-2xl bg-[rgba(18,18,23,0.9)] backdrop-blur-xl border border-white/15 p-5 shadow-2xl text-center"
      data-testid="portfolio-preview-cta-upsell"
    >
      <div className="mx-auto inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#3DEBFF]/15 border border-[#3DEBFF]/40 mb-2.5">
        <Lock className="w-4 h-4 text-[#3DEBFF]" />
      </div>
      <div className="font-display text-lg mb-1">
        {tt("portfolio.upsell.title", "Unlock Portfolio Intelligence")}
      </div>
      <p className="text-xs text-muted-foreground mb-3.5 leading-relaxed">
        {tt("portfolio.upsell.body", "See valuation, performance and risk across your entire portfolio.")}
      </p>
      <Button
        size="sm"
        onClick={onUpgrade}
        data-testid="portfolio-upgrade-cta-button"
        className="shadow-[0_0_0_1.5px_rgba(61,235,255,0.5),0_0_16px_rgba(61,235,255,0.22)]"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
        {tt("portfolio.upsell.cta", "Explore Premium")}
        <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
      <div className="mt-2.5 text-[11px] text-muted-foreground leading-snug">
        {tt("portfolio.upsell.footnote", "You'll review features and pricing before checkout. No charge occurs at this step.")}
      </div>
    </div>
  );
}

export default function LockedPortfolioPreview({
  onUpgrade,
  variant = "upsell",
  onSignup,
  onLogin,
  onViewPricing,
}) {
  const { t } = useTranslation();
  const isLoggedOut = variant === "logged_out";
  const tt = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };

  const ctaCard = (
    <CtaCard
      isLoggedOut={isLoggedOut}
      onSignup={onSignup}
      onLogin={onLogin}
      onUpgrade={onUpgrade}
      onViewPricing={onViewPricing || onUpgrade}
      t={t}
    />
  );

  return (
    <div className="relative space-y-6 sm:space-y-8" data-testid="portfolio-locked-preview" data-variant={variant}>
      {/* Preview badge — signals demo data. */}
      <div
        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-card/40 px-3 py-1 text-[11px] text-muted-foreground"
        data-testid="portfolio-preview-badge"
      >
        <Info className="w-3 h-3 text-[#3DEBFF]" />
        <span className="uppercase tracking-[0.18em] font-medium text-foreground/80">
          {tt("portfolio.locked.demo.badge", "Portfolio preview")}
        </span>
        <span>&middot;</span>
        <span>{tt("portfolio.locked.demo.badge_hint", "Example data. Not linked to a real account.")}</span>
      </div>

      {/* Shared presentational tree — identical layout to Premium. */}
      <div className="space-y-6 sm:space-y-8" data-testid="portfolio-preview-dashboard">
        <PortfolioDashboardView
          data={DEMO_DATA}
          closedPositions={DEMO_CLOSED_POSITIONS}
          dominantCurrency="USD"
          previewMode={true}
          refreshAnalysisBusy={false}
          ctaOverlay={ctaCard}
          chartFetchOverride={demoPerformanceFetchOverride}
        />
      </div>
    </div>
  );
}
